/**
 * Utility functions for working with workers
 */

import {
  isJobTimerActive,
  isOpenActiveJob,
  isPendingQueueJob,
  isScheduledOpenJob
} from './displayUtils';
import { isWorkerInactiveOnDate, isWorkerAvailableForWork } from './workerAvailabilityUtils';

/**
 * Get worker or partner name from ID
 * @param {string|object} workerId - Worker/Partner ID or object (can be prefixed with "worker:" or "partner:")
 * @param {Array} workers - Array of all workers
 * @param {Array} partners - Array of all partners
 * @returns {string} Worker/Partner name or fallback text
 */
const findTeamById = (teamId, teams = []) =>
  teams.find((team) => String(team._id || team.id || '') === String(teamId));

export const getTeamMemberIds = (team) =>
  (team?.members || []).map((member) =>
    String(typeof member === 'object' ? member._id || member.id : member)
  );

export const resolveTeamMembers = (team, workers = []) =>
  getTeamMemberIds(team)
    .map((id) => (workers || []).find((w) => String(w._id || w.id) === id))
    .filter(Boolean);

export const getWorkerName = (workerId, workers = [], partners = [], teams = []) => {
  // Handle populated object from server
  if (typeof workerId === 'object' && workerId !== null) {
    // Check if it's a partner (has companyName)
    if (workerId.companyName) {
      return workerId.companyName || 'Nepoznat partner';
    }
    // Otherwise it's a worker
    const name = `${workerId.firstName || ''} ${workerId.lastName || ''}`.trim();
    return name || workerId.email || 'Nepoznat radnik';
  }
  
  // Handle string ID - check for prefix (worker: or partner:)
  if (typeof workerId === 'string' && workerId !== 'Unassigned' && workerId !== '') {
    // Check if it has a prefix
    if (workerId.startsWith('partner:')) {
      const partnerId = workerId.replace('partner:', '');
      const partner = partners.find(p => {
        const partnerIdStr = String(p._id || p.id || '');
        return partnerIdStr === partnerId;
      });
      
      if (partner) {
        return partner.companyName || 'Nepoznat partner';
      }
      return 'Nepoznat partner';
    } else if (workerId.startsWith('team:')) {
      const teamId = workerId.replace('team:', '');
      const team = findTeamById(teamId, teams);
      return team?.name || 'Nepoznat tim';
    } else if (workerId.startsWith('worker:')) {
      const actualWorkerId = workerId.replace('worker:', '');
      const worker = workers.find(w => {
        const workerIdStr = String(w._id || w.id || '');
        return workerIdStr === actualWorkerId;
      });
      
      if (worker) {
        const name = `${worker.firstName || ''} ${worker.lastName || ''}`.trim();
        return name || worker.email || 'Nepoznat radnik';
      }
      return 'Nepoznat radnik';
    } else {
      // Legacy format - try to find in workers first, then partners
      const worker = workers.find(w => {
        const workerIdStr = String(w._id || w.id || '');
        const searchIdStr = String(workerId);
        return workerIdStr === searchIdStr;
      });
      
      if (worker) {
        const name = `${worker.firstName || ''} ${worker.lastName || ''}`.trim();
        return name || worker.email || 'Nepoznat radnik';
      }
      
      // Try partners
      const partner = partners.find(p => {
        const partnerIdStr = String(p._id || p.id || '');
        const searchIdStr = String(workerId);
        return partnerIdStr === searchIdStr;
      });
      
      if (partner) {
        return partner.companyName || 'Nepoznat partner';
      }
    }
  }
  
  // Fallback
  if (workerId === 'Unassigned' || workerId === '') {
    return 'Nedodeljeno';
  }
  
  return 'Nepoznat radnik';
};

/**
 * Normalize worker ID for comparison (handles both ObjectId and string)
 * @param {string|object} workerId - Worker ID
 * @returns {string} Normalized ID string
 */
export const normalizeWorkerId = (workerId) => {
  if (!workerId) return null;
  if (typeof workerId === 'object' && workerId !== null) {
    return String(workerId._id || workerId.id || '');
  }
  return String(workerId);
};

/**
 * Check if a job is assigned to a specific worker or partner
 * @param {object} job - Job object
 * @param {string|object} workerId - Worker/Partner ID to check
 * @returns {boolean} True if job is assigned to worker/partner
 */
export const isJobAssignedToWorker = (job, workerId, teams = []) => {
  if (!job?.assignedTo || job.assignedTo === 'Unassigned' || job.assignedTo === '') {
    return false;
  }
  
  // Handle populated object from server
  if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
    const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '');
    const normalizedWorkerId = normalizeWorkerId(workerId);
    return assignedToId === normalizedWorkerId;
  }
  
  // Handle string ID (with or without prefix)
  const assignedToStr = String(job.assignedTo);
  const normalizedWorkerId = normalizeWorkerId(workerId);
  
  // Direct ID match
  if (assignedToStr === normalizedWorkerId) {
    return true;
  }
  
  // Check if assignedTo has "worker:" or "partner:" prefix
  if (assignedToStr.startsWith('worker:')) {
    const workerIdFromAssigned = assignedToStr.replace('worker:', '');
    return workerIdFromAssigned === normalizedWorkerId;
  }
  
  if (assignedToStr.startsWith('partner:')) {
    const partnerIdFromAssigned = assignedToStr.replace('partner:', '');
    return partnerIdFromAssigned === normalizedWorkerId;
  }

  if (assignedToStr.startsWith('team:')) {
    const teamId = assignedToStr.replace('team:', '');
    const team = findTeamById(teamId, teams);
    if (!team) return false;
    return getTeamMemberIds(team).includes(normalizedWorkerId);
  }
  
  // Legacy name matching (for backward compatibility) - skip for partners
  // Partners should always use ID format
  
  const isMatch = assignedToStr === normalizedWorkerId;
  
  // Debug logging (can be removed later)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 isJobAssignedToWorker:', {
      jobId: job._id,
      jobAssignedTo: job.assignedTo,
      assignedToStr,
      normalizedJobAssignedTo: assignedToStr,
      workerId,
      normalizedWorkerId,
      isMatch
    });
  }
  
  return isMatch;
};

/**
 * Get worker color from ID
 * @param {string|object} workerId - Worker ID or object
 * @param {Array} workers - Array of all workers
 * @returns {string} Worker color or default blue color
 */
export const getWorkerColor = (workerId, workers = [], teams = []) => {
  // Handle populated object from server
  if (typeof workerId === 'object' && workerId !== null) {
    return workerId.color || '#3B82F6';
  }
  
  // Handle string ID
  if (typeof workerId === 'string' && workerId !== 'Unassigned' && workerId !== '') {
    if (workerId.startsWith('team:')) {
      return '#9333EA';
    }
    if (workerId.startsWith('worker:')) {
      const actualWorkerId = workerId.replace('worker:', '');
      const worker = workers.find(w => {
        const workerIdStr = String(w._id || w.id || '');
        return workerIdStr === actualWorkerId;
      });
      
      if (worker) {
        return worker.color || '#3B82F6';
      }
    } else {
      // Legacy format - try to find in workers
      const worker = workers.find(w => {
        const workerIdStr = String(w._id || w.id || '');
        const searchIdStr = String(workerId);
        return workerIdStr === searchIdStr;
      });
      
      if (worker) {
        return worker.color || '#3B82F6';
      }
    }
  }
  
  // Fallback to default blue
  return '#3B82F6';
};

/** Boja dodeljenog radnika, tima ili servisnog partnera. */
export const getAssigneeColor = (assigneeId, workers = [], partners = [], teams = []) => {
  if (!assigneeId) return '#3B82F6';

  if (typeof assigneeId === 'object' && assigneeId !== null) {
    if (assigneeId.companyName) {
      return assigneeId.color || '#F97316';
    }
    return assigneeId.color || '#3B82F6';
  }

  if (typeof assigneeId === 'string') {
    if (assigneeId.startsWith('partner:')) {
      const partnerId = assigneeId.replace('partner:', '');
      const partner = partners.find(
        (p) => String(p._id || p.id || '') === partnerId
      );
      return partner?.color || '#F97316';
    }
  }

  return getWorkerColor(assigneeId, workers, teams);
};

/**
 * Get worker initials from name
 * @param {string|object} workerId - Worker ID or object
 * @param {Array} workers - Array of all workers
 * @param {Array} partners - Array of all partners
 * @returns {string} Worker initials (e.g., "MM" for "Milan Maric")
 */
export const getWorkerInitials = (workerId, workers = [], partners = [], teams = []) => {
  // Handle populated object from server
  if (typeof workerId === 'object' && workerId !== null) {
    // Check if it's a partner (has companyName)
    if (workerId.companyName) {
      // For partners, use first 2 letters of company name
      return workerId.companyName.substring(0, 2).toUpperCase();
    }
    // Otherwise it's a worker
    const firstName = workerId.firstName || '';
    const lastName = workerId.lastName || '';
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (workerId.email) {
      return workerId.email.substring(0, 2).toUpperCase();
    }
    return '??';
  }
  
  // Handle string ID
  if (typeof workerId === 'string' && workerId !== 'Unassigned' && workerId !== '') {
    // Check if it has a prefix
    if (workerId.startsWith('partner:')) {
      const partnerId = workerId.replace('partner:', '');
      const partner = partners.find(p => {
        const partnerIdStr = String(p._id || p.id || '');
        return partnerIdStr === partnerId;
      });
      
      if (partner && partner.companyName) {
        return partner.companyName.substring(0, 2).toUpperCase();
      }
      return '??';
    } else if (workerId.startsWith('team:')) {
      const teamId = workerId.replace('team:', '');
      const team = findTeamById(teamId, teams);
      if (team?.name) {
        return team.name.substring(0, 2).toUpperCase();
      }
      return 'TM';
    } else if (workerId.startsWith('worker:')) {
      const actualWorkerId = workerId.replace('worker:', '');
      const worker = workers.find(w => {
        const workerIdStr = String(w._id || w.id || '');
        return workerIdStr === actualWorkerId;
      });
      
      if (worker) {
        const firstName = worker.firstName || '';
        const lastName = worker.lastName || '';
        if (firstName && lastName) {
          return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        } else if (firstName) {
          return firstName.substring(0, 2).toUpperCase();
        } else if (worker.email) {
          return worker.email.substring(0, 2).toUpperCase();
        }
      }
      return '??';
    } else {
      // Legacy format - try to find in workers first, then partners
      const worker = workers.find(w => {
        const workerIdStr = String(w._id || w.id || '');
        const searchIdStr = String(workerId);
        return workerIdStr === searchIdStr;
      });
      
      if (worker) {
        const firstName = worker.firstName || '';
        const lastName = worker.lastName || '';
        if (firstName && lastName) {
          return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        } else if (firstName) {
          return firstName.substring(0, 2).toUpperCase();
        } else if (worker.email) {
          return worker.email.substring(0, 2).toUpperCase();
        }
      }
      
      // Try partners
      const partner = partners.find(p => {
        const partnerIdStr = String(p._id || p.id || '');
        const searchIdStr = String(workerId);
        return partnerIdStr === searchIdStr;
      });
      
      if (partner && partner.companyName) {
        return partner.companyName.substring(0, 2).toUpperCase();
      }
    }
  }
  
  // Fallback
  if (workerId === 'Unassigned' || workerId === '') {
    return '-';
  }
  
  return '??';
};

const WORKER_DAY_SKIP_STATUSES = ['Cancelled', 'Archived'];

export const getWorkerMaxDailyInterventions = (worker) => {
  const n = Number(worker?.maxDailyInterventions);
  if (!Number.isFinite(n) || n < 1) return 10;
  return Math.min(50, Math.round(n));
};

const toLocalDayKey = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const isoDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDate) return isoDate[1];
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const isSameCalendarDay = (dateA, dateB) => {
  const keyA = toLocalDayKey(dateA);
  const keyB = toLocalDayKey(dateB);
  return Boolean(keyA && keyB && keyA === keyB);
};

/**
 * Broj poslova dodeljenih radniku za izabrani dan — samo sa datumom servisa tog dana.
 * Nezakazani (bez serviceDate) se ne uračunavaju u dnevni broj 3/10.
 */
export const countWorkerDayAssignments = (jobs, workerId, referenceDate = new Date()) => {
  if (!Array.isArray(jobs) || !workerId) return 0;

  return jobs.filter((job) => {
    if (!isJobAssignedToWorker(job, workerId)) return false;
    if (WORKER_DAY_SKIP_STATUSES.includes(job.status)) return false;
    if (!job.serviceDate) return false;
    return isSameCalendarDay(job.serviceDate, referenceDate);
  }).length;
};

/** Da li je posao eksplicitno dodeljen timu (assignedTo = team:id). */
export const isJobAssignedToTeam = (job, teamId) => {
  if (!job?.assignedTo || !teamId) return false;

  const normalizedTeamId = String(teamId);
  if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
    return String(job.assignedTo._id || job.assignedTo.id || '') === normalizedTeamId;
  }

  const assignedToStr = String(job.assignedTo);
  return assignedToStr === `team:${normalizedTeamId}` || assignedToStr === normalizedTeamId;
};

/** Broj poslova dodeljenih timu za izabrani dan. */
export const countTeamDayAssignments = (jobs, teamId, referenceDate = new Date()) => {
  if (!Array.isArray(jobs) || !teamId) return 0;

  return jobs.filter((job) => {
    if (!isJobAssignedToTeam(job, teamId)) return false;
    if (WORKER_DAY_SKIP_STATUSES.includes(job.status)) return false;
    if (!job.serviceDate) return false;
    return isSameCalendarDay(job.serviceDate, referenceDate);
  }).length;
};

/** Zbir dnevnog kapaciteta članova tima koji su dostupni tog dana. */
export const getTeamMaxDailyInterventions = (team, workers = [], referenceDate = new Date()) => {
  const memberIds = getTeamMemberIds(team);
  if (memberIds.length === 0) return 0;

  return memberIds.reduce((sum, memberId) => {
    const worker = (workers || []).find((w) => String(w._id || w.id) === memberId);
    if (!worker || worker.active === false) return sum;
    if (!isWorkerAvailableForWork(worker, referenceDate)) return sum;
    return sum + getWorkerMaxDailyInterventions(worker);
  }, 0);
};

export const getTeamMemberCount = (team) => getTeamMemberIds(team).length;

/** Zakazani otvoreni posao za izabrani kalendarski dan. */
export const isJobScheduledForDate = (job, referenceDate = new Date()) =>
  Boolean(job?.serviceDate) && isOpenActiveJob(job) && isSameCalendarDay(job.serviceDate, referenceDate);

export const countJobsScheduledForDate = (jobs, referenceDate = new Date()) => {
  if (!Array.isArray(jobs)) return 0;
  return jobs.filter((job) => isJobScheduledForDate(job, referenceDate)).length;
};

const addCalendarDays = (date, days) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d;
};

/** Ponedeljak 00:00 nedelje u kojoj pada referentni datum. */
const startOfWeekMonday = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

/**
 * Kategorija zakazanog posla: danas, sutra, za dva dana, sledeća nedelja, ostalo.
 * Na čekanju se obrađuje posebno (bez serviceDate).
 */
export const getScheduledJobHorizonBucket = (job, referenceDate = new Date()) => {
  if (!isScheduledOpenJob(job)) return null;

  const ref = new Date(referenceDate);
  ref.setHours(0, 0, 0, 0);
  const jobKey = toLocalDayKey(job.serviceDate);
  if (!jobKey) return 'ostalo';

  if (jobKey === toLocalDayKey(ref)) return 'danas';
  if (jobKey === toLocalDayKey(addCalendarDays(ref, 1))) return 'sutra';
  if (jobKey === toLocalDayKey(addCalendarDays(ref, 2))) return 'zaDvaDana';

  const jobDate = new Date(job.serviceDate);
  jobDate.setHours(0, 0, 0, 0);
  const nextWeekStart = addCalendarDays(startOfWeekMonday(ref), 7);
  const nextWeekEnd = addCalendarDays(nextWeekStart, 6);

  if (jobDate >= nextWeekStart && jobDate <= nextWeekEnd) {
    return 'sledecaNedelja';
  }

  return 'ostalo';
};

export const countScheduledJobsByHorizon = (jobs, referenceDate = new Date()) => {
  const counts = {
    danas: 0,
    sutra: 0,
    zaDvaDana: 0,
    sledecaNedelja: 0,
    ostalo: 0,
    naCekanju: 0
  };

  if (!Array.isArray(jobs)) return counts;

  jobs.forEach((job) => {
    if (isPendingQueueJob(job)) {
      counts.naCekanju += 1;
      return;
    }
    const bucket = getScheduledJobHorizonBucket(job, referenceDate);
    if (bucket && counts[bucket] !== undefined) {
      counts[bucket] += 1;
    }
  });

  return counts;
};

/**
 * Broj majstora koji trenutno rade (aktivan tajmer puta ili rada).
 * Maksimum = broj radnika — jedan majstor ne može imati više od jedne aktivne intervencije.
 */
export const countWorkersWithActiveTimer = (jobs, workers) => {
  if (!Array.isArray(jobs) || !Array.isArray(workers) || workers.length === 0) return 0;

  const activeWorkers = workers.filter((worker) => worker?.active !== false);
  return activeWorkers.reduce((count, worker) => {
    const workerId = worker?._id;
    if (!workerId) return count;
    const hasActive = jobs.some(
      (job) => isJobAssignedToWorker(job, workerId) && isJobTimerActive(job)
    );
    return count + (hasActive ? 1 : 0);
  }, 0);
};

/** Pregled radnika: neaktivni (kalendar), aktivni, u vožnji, u radu, na pauzi. */
export const summarizeWorkerStatuses = (workers, getStatus, referenceDate = new Date()) => {
  const activeWorkers = (workers || []).filter((worker) => worker?.active !== false);
  const stats = {
    ukupno: activeWorkers.length,
    aktivni: 0,
    neaktivni: 0,
    odmorBolovanje: 0,
    uVoznji: 0,
    uRadu: 0,
    naPauzi: 0
  };

  activeWorkers.forEach((worker) => {
    if (isWorkerInactiveOnDate(worker, referenceDate)) {
      stats.neaktivni += 1;
      stats.odmorBolovanje += 1;
      return;
    }

    const status = typeof getStatus === 'function' ? getStatus(worker) : 'available';
    if (status === 'on_break') stats.naPauzi += 1;
    else if (status === 'on_the_road') stats.uVoznji += 1;
    else if (status === 'in_workshop' || status === 'at_client') stats.uRadu += 1;
    else stats.aktivni += 1;
  });

  return stats;
};

/** Broj dostupnih radnika čije je dnevno opterećenje >90% (n/max). */
export const countOverloadedWorkers = (jobs, workers, referenceDate = new Date()) => {
  const activeWorkers = (workers || []).filter((worker) => worker?.active !== false);

  return activeWorkers.reduce((count, worker) => {
    if (isWorkerInactiveOnDate(worker, referenceDate)) return count;
    const max = getWorkerMaxDailyInterventions(worker);
    if (!max || max < 1) return count;
    const assigned = countWorkerDayAssignments(jobs, worker._id, referenceDate);
    return count + ((assigned / max) * 100 > 90 ? 1 : 0);
  }, 0);
};

/** Agregirano dnevno opterećenje: zauzeto / kapacitet aktivnih radnika za dan. */
export const summarizeTeamDailyLoad = (workers, jobs, referenceDate = new Date()) => {
  const activeWorkers = (workers || []).filter((worker) => worker?.active !== false);
  const availableWorkers = activeWorkers.filter((worker) =>
    isWorkerAvailableForWork(worker, referenceDate)
  );

  const capacity = availableWorkers.reduce(
    (sum, worker) => sum + getWorkerMaxDailyInterventions(worker),
    0
  );
  const used = countJobsScheduledForDate(jobs, referenceDate);

  const pct = capacity > 0 ? (used / capacity) * 100 : 0;

  return { used, capacity, pct };
};

const getLoadPctColorClasses = (pct) => {
  if (pct > 90) return { text: 'text-red-400', bar: 'bg-red-500' };
  if (pct >= 71) return { text: 'text-orange-400', bar: 'bg-orange-500' };
  if (pct >= 51) return { text: 'text-green-400', bar: 'bg-green-500' };
  return { text: 'text-white', bar: 'bg-slate-400' };
};

/** Boja opterećenja: ≤50% belo, 51–70% zeleno, 71–90% narandžasto, >90% crveno. */
export const getWorkerLoadColorClass = (count, max) => {
  if (!max || max < 1) return 'text-white';
  return getLoadPctColorClasses((count / max) * 100).text;
};

/** Boja progress bara za agregirano dnevno opterećenje. */
export const getWorkerLoadBarColorClass = (used, capacity) => {
  if (!capacity || capacity < 1) return 'bg-slate-500';
  return getLoadPctColorClasses((used / capacity) * 100).bar;
};
